import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
function date(value) {
    return value ? value.toDate().toLocaleString() : 'Not recorded';
}
export function sourceState(source) {
    switch(source.failureCode){
        case 'authentication_unavailable':
        case 'authentication_failed':
            return 'Auth unavailable';
        case 'allowance_exhausted':
            return 'Refresh budget exhausted';
        case 'public_policy_unverified':
            return 'Public access not verified';
        case 'provider_window_incomplete':
        case 'partial_failure':
            return 'Partial refresh';
        case 'provider_unavailable':
            return 'Provider unavailable';
        case 'invalid_payload':
            return 'Provider response could not be used';
    }
    if (source.failureCode) return 'Refresh problem';
    if (source.syncStatus === 'failed') return 'Refresh failed';
    if (source.syncStatus === 'running') return 'Refreshing';
    if (source.syncStatus === 'partial') return 'Partial refresh';
    if (source.successfulEmpty) return 'Successful empty bounded check';
    if (source.syncStatus === 'complete') return 'Completed bounded refresh';
    return 'Availability unknown';
}
export function SourceStatusPanel({ sources = [], loading = false, failed = false }) {
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Source status",
        className: "space-y-3 rounded-md border p-3 text-sm",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-medium",
                children: "Source status"
            }),
            loading ? /*#__PURE__*/ _jsx("output", {
                "data-loading": "true",
                children: "Loading source status…"
            }) : failed ? /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Source status could not be loaded. Availability is unknown; try reloading."
            }) : sources.length === 0 ? /*#__PURE__*/ _jsx("p", {
                children: "Source status is unavailable for this view. This does not establish an empty dataset."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-3",
                children: sources.map((source)=>/*#__PURE__*/ _jsxs("li", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                className: "font-medium",
                                children: [
                                    source.source,
                                    " — ",
                                    sourceState(source)
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: source.scope
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Coverage: ",
                                    source.coverage,
                                    ". Applies only to the recorded query scope."
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Last successful refresh: ",
                                    date(source.lastSuccessfulAt)
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Public cache evidence as of: ",
                                    date(source.evidenceAsOf)
                                ]
                            }),
                            source.freshness === 'stale' && /*#__PURE__*/ _jsx("p", {
                                children: "Stale refresh: the successful check or its evidence exceeds the cache freshness policy."
                            }),
                            source.freshness === 'fresh' && /*#__PURE__*/ _jsx("p", {
                                children: "Public cache freshness: fresh."
                            }),
                            source.lastAttemptedAt && /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Last provider attempt: ",
                                    date(source.lastAttemptedAt)
                                ]
                            }),
                            source.resultCount !== undefined && /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Results in this attempt: ",
                                    String(source.resultCount),
                                    "."
                                ]
                            }),
                            source.truncated && /*#__PURE__*/ _jsx("p", {
                                children: "Results were truncated; this query is incomplete."
                            }),
                            (source.failureCode || source.syncStatus === 'failed' || source.syncStatus === 'partial') && /*#__PURE__*/ _jsx("p", {
                                children: "Prior evidence retained, subject to your existing access. A failed or partial refresh does not mean no awards or no access."
                            }),
                            source.successfulEmpty && /*#__PURE__*/ _jsx("p", {
                                children: "The completed bounded query returned no results. Prior observations remain available. This does not establish no awards, incumbency or eligibility."
                            }),
                            Number(source.explicitRetractions) > 0 && /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Explicit source retractions with deletion evidence:",
                                    ' ',
                                    String(source.explicitRetractions),
                                    ". Retained history remains available."
                                ]
                            })
                        ]
                    }, `${source.source}:${source.scope}`))
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Cache freshness is separate from a fact's effective or reviewed date. Disappearance alone does not establish expiry or deletion."
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Manual Company creation remains available through Add entity."
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Owners: ",
                    /*#__PURE__*/ _jsx(Link, {
                        to: "/delivery/registries",
                        children: "C1 shared identities"
                    }),
                    ";",
                    ' ',
                    /*#__PURE__*/ _jsx(Link, {
                        to: "/capture",
                        children: "Track B opportunity interpretation"
                    }),
                    ". C2 owns definitions; C3 mappings; C4 rates; C6 shared evidence; Delivery owns operational records. Source evidence does not approve changes in those domains."
                ]
            })
        ]
    });
}
